import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Close as CloseIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { ru, enUS } from 'date-fns/locale'
import { useTranslate, useLocaleState } from 'react-admin'
import { useAudit } from 'hooks/useAudit'
import UserReference from './user.reference'

interface FieldAuditModalProps {
  isOpen: boolean
  onClose: () => void
  tableName: string
  recordId: string
  fieldName: string
  fieldLabel?: string
  currentValue?: any
  currentDisplayValue?: any; // Добавляем поддержку display-значения для текущего значения
}

interface FieldAuditLog {
  id: string
  action: 'CREATE' | 'UPDATE' | 'DELETE'
  timestamp: string
  user_name?: string
  user_email?: string
  user_id?: string
  old_value: any
  new_value: any
  old_display?: any
  new_display?: any
  type?: string
}

const FieldAuditModal: React.FC<FieldAuditModalProps> = ({
  isOpen,
  onClose,
  tableName,
  recordId,
  fieldName,
  fieldLabel,
  currentValue,
  currentDisplayValue
}) => {
  const [logs, setLogs] = useState<FieldAuditLog[]>([])
  const [currentDisplay, setCurrentDisplay] = useState<any>(null)
  const { fetchFieldHistory, loading, error } = useAudit()
  const translate = useTranslate()
  const [locale] = useLocaleState()

  const loadFieldHistory = useCallback(async () => {
    if (!isOpen) return
    
    try {
      const data = await fetchFieldHistory(tableName, recordId, fieldName)
      setLogs(data || [])
      
      // Если есть история и это FK поле, попробуем получить display-значение для текущего значения
      if (data && data.length > 0 && currentValue && !currentDisplayValue) {
        const firstLog = data[0]
        if (firstLog.type === 'fk_array' || firstLog.type === 'fk') {
          // Попробуем найти текущее значение в истории
          const currentLogEntry = data.find(log => 
            log.new_value === currentValue || log.old_value === currentValue
          )
          
          if (currentLogEntry) {
            if (currentLogEntry.new_value === currentValue && currentLogEntry.new_display) {
              setCurrentDisplay(currentLogEntry.new_display)
            } else if (currentLogEntry.old_value === currentValue && currentLogEntry.old_display) {
              setCurrentDisplay(currentLogEntry.old_display)
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching field history:', error)
    }
  }, [isOpen, tableName, recordId, fieldName, fetchFieldHistory, currentValue, currentDisplayValue])

  useEffect(() => {
    loadFieldHistory()
  }, [loadFieldHistory])

  const formatValue = (value: any, displayValue?: any): string => {
    // Если есть display значение, используем его
    if (displayValue) {
      // Если это объект с name, показываем name
      if (typeof displayValue === 'object' && displayValue.name) {
        return displayValue.name
      }
      // Если это строка, показываем как есть
      if (typeof displayValue === 'string') {
        return displayValue
      }
      // В остальных случаях преобразуем в JSON
      return JSON.stringify(displayValue)
    }
    
    // Если нет display значения, обрабатываем само значение
    if (value === null || value === undefined) return '—'
    if (typeof value === 'boolean') return value ? translate('resources.audit.yes') : translate('resources.audit.no')
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  const formatDateTime = (timestamp: string) => {
    const dateLocale = locale === 'ru' ? ru : enUS
    return format(new Date(timestamp), 'dd MMMM yyyy, HH:mm', { locale: dateLocale })
  }

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="div">
              {fieldLabel || fieldName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Текущее значение - показываем только если есть история */}
        {!loading && !error && logs.length > 0 && (
          <Card variant="outlined" sx={{ mb: 2, bgcolor: 'primary.50' }}>
            <CardContent>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {translate('resources.audit.current_value')}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatValue(currentValue, currentDisplayValue || currentDisplay)}
              </Typography>
            </CardContent>
          </Card>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {translate('resources.audit.loading')}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {logs.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  {translate('resources.audit.no_history')}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{translate('resources.audit.was')}</strong></TableCell>
                      <TableCell><strong>{translate('resources.audit.became')}</strong></TableCell>
                      <TableCell><strong>{translate('resources.audit.date_time')}</strong></TableCell>
                      <TableCell><strong>{translate('resources.audit.user')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Box 
                              sx={{ 
                                p: 1, 
                                bgcolor: 'error.50', 
                                borderRadius: 1,
                                color: 'error.main'
                              }}
                            >
                              <Typography>
                                {formatValue(log.old_value, log.old_display)}
                              </Typography>
                            </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box 
                              sx={{ 
                                p: 1, 
                                bgcolor: 'success.50', 
                                borderRadius: 1,
                                color: 'success.main'
                              }}
                            >
                              <Typography>
                                {formatValue(log.new_value, log.new_display)}
                              </Typography>
                            </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography>
                            {formatDateTime(log.timestamp)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <UserReference 
                            userId={log.user_id} 
                            fallback={log.user_name || log.user_email || translate('resources.audit.system')} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {translate('resources.audit.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FieldAuditModal
